import React, { useEffect, useState, useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { Media } from '../../../models/Media/Media';
import { Pagination } from '../../../models/Media/contracts';
import { MediaUpload } from '../../components/MediaUpload/MediaUpload';
import { ButtonPrimary } from '../Button/ButtonPrimary';
import { SimpleModal } from '../Modals/SimpleModal/SimpleModal';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import styles from './media-library-selector.module.scss';
import { toast } from 'react-toastify';
import {
  FaAngleLeft,
  FaAngleRight,
  FaSearch,
  FaTrash,
  FaCheck
} from 'react-icons/fa';

// Helper hook to generate page numbers for pagination control
const usePaginationRange = (
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
) => {
  return useMemo(() => {
    const totalPageNumbers = siblingCount + 5;
    if (totalPageNumbers >= totalPages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;
    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1
      );
      return [firstPageIndex, '...', ...rightRange];
    }
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i
      );
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
    }
    return [];
  }, [currentPage, totalPages, siblingCount]);
};

interface MediaLibrarySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedMedia: Media[] | Media) => void;
  multi?: boolean;
  title?: string;
}

export const MediaLibrarySelector: React.FC<MediaLibrarySelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  multi = false,
  title = 'Izaberi sliku iz biblioteke'
}) => {
  const [mediaItems, setMediaItems] = useState<Media[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedForPreview, setSelectedForPreview] = useState<Media | null>(
    null
  );
  const [editableName, setEditableName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedForOutputIds, setSelectedForOutputIds] = useState<number[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState<'library' | 'upload'>('library');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<Media | null>(null);

  const paginationRange = usePaginationRange(
    currentPage,
    pagination?.last_page || 1
  );

  const fetchMedia = async (page: number, search: string, limit: number) => {
    setIsLoading(true);
    setSelectedForPreview(null);
    try {
      const response = await Media.all(page, search, limit);
      setMediaItems(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Neuspelo učitavanje medija.');
      console.error('Failed to fetch media:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && activeTab === 'library') {
      fetchMedia(currentPage, debouncedSearchTerm, perPage);
    }
  }, [isOpen, activeTab, debouncedSearchTerm, currentPage, perPage]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('library');
      setSelectedForOutputIds([]);
      setSelectedForPreview(null);
      setSearchTerm('');
      setCurrentPage(1);
      setShowDeleteConfirm(false);
      setMediaToDelete(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedForPreview) {
      setEditableName(selectedForPreview.name);
    }
  }, [selectedForPreview]);

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number | string) => {
    if (typeof pageNumber === 'number') {
      setCurrentPage(pageNumber);
    }
  };

  const handleUpdateName = async () => {
    if (!selectedForPreview || editableName === selectedForPreview.name) return;
    setIsSaving(true);
    try {
      const updatedMedia = await Media.update(selectedForPreview.id, {
        name: editableName
      });
      setMediaItems((currentItems) =>
        currentItems.map((item) =>
          item.id === updatedMedia.id ? new Media(updatedMedia) : item
        )
      );
      setSelectedForPreview(new Media(updatedMedia));
      toast.success('Naziv slike je uspešno izmenjen!');
    } catch (error) {
      toast.error('Izmena naziva nije uspela.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (media: Media) => {
    setMediaToDelete(media);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!mediaToDelete) return;
    try {
      await Media.delete(mediaToDelete.id);
      setMediaItems((currentItems) =>
        currentItems.filter((item) => item.id !== mediaToDelete.id)
      );
      setSelectedForPreview(null);
      toast.success('Slika je uspešno obrisana!');
    } catch (error) {
      toast.error('Brisanje slike nije uspelo.');
    } finally {
      setShowDeleteConfirm(false);
      setMediaToDelete(null);
    }
  };

  const handleUploadComplete = () => {
    toast.success('Fajlovi su uspešno dodati!');
    setActiveTab('library');
    setCurrentPage(1);
    fetchMedia(1, '', perPage);
  };

  const handleConfirm = () => {
    const selectedItems = mediaItems.filter((item) =>
      selectedForOutputIds.includes(item.id)
    );
    if (selectedItems.length === 0) return;
    onSelect(multi ? selectedItems : selectedItems[0]);
    onClose();
  };

  const handleSelectForOutput = (id: number) => {
    if (multi) {
      setSelectedForOutputIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    } else {
      setSelectedForOutputIds([id]);
    }
  };

  return (
    <>
      <SimpleModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        dialogClassName={styles.fullScreenDialog}
        contentClassName={styles.fullScreenContent}
      >
        <div className={styles.container}>
          <div className={styles.mainContent}>
            <div className={styles.tabs}>
              <button
                className={`${styles.tabButton} ${
                  activeTab === 'library' ? styles.active : ''
                }`}
                onClick={() => setActiveTab('library')}
              >
                Biblioteka
              </button>
              <button
                className={`${styles.tabButton} ${
                  activeTab === 'upload' ? styles.active : ''
                }`}
                onClick={() => setActiveTab('upload')}
              >
                Dodaj Nove Fajlove
              </button>
            </div>
            {activeTab === 'library' && (
              <div className={styles.libraryView}>
                <div className={styles.header}>
                  <div className={styles.searchWrapper}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Pretraži po nazivu..."
                      className={styles.searchInput}
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <div className={styles.perPageSelector}>
                    <label htmlFor="perPage">Prikaži po:</label>
                    <select
                      id="perPage"
                      value={perPage}
                      onChange={handlePerPageChange}
                      className={styles.selectDropdown}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                {isLoading ? (
                  <div className={styles.loader}>Učitavanje...</div>
                ) : (
                  <div className={styles.gridContainer}>
                    <div className={styles.grid}>
                      {mediaItems.map((item) => {
                        const isSelected = selectedForOutputIds.includes(
                          item.id
                        );
                        return (
                          <div
                            key={item.id}
                            className={`${styles.gridItem} ${
                              isSelected ? styles.selected : ''
                            }`}
                            onClick={() => setSelectedForPreview(item)}
                          >
                            <img
                              src={item.fullUrl}
                              alt={item.name}
                              className={styles.image}
                              loading="lazy"
                            />
                            <div
                              className={styles.checkboxWrapper}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevents the grid item's onClick from firing
                                handleSelectForOutput(item.id);
                              }}
                            >
                              <div className={styles.customCheckbox}>
                                {isSelected && (
                                  <FaCheck className={styles.checkIcon} />
                                )}
                              </div>
                              <input
                                type={multi ? 'checkbox' : 'radio'}
                                name="media-selection"
                                checked={isSelected}
                                readOnly // The click is handled by the wrapper div
                                className={styles.hiddenInput}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {pagination && pagination.total > 0 && !isLoading && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.pageArrow}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      <FaAngleLeft /> Prethodna
                    </button>
                    {paginationRange.map((pageNumber, index) => {
                      if (pageNumber === '...') {
                        return (
                          <span
                            key={`${pageNumber}-${index}`}
                            className={styles.ellipsis}
                          >
                            ...
                          </span>
                        );
                      }
                      return (
                        <button
                          key={pageNumber}
                          className={`${styles.pageItem} ${
                            pageNumber === currentPage ? styles.active : ''
                          }`}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    <button
                      className={styles.pageArrow}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= pagination.last_page}
                    >
                      Sledeća <FaAngleRight />
                    </button>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'upload' && (
              <div className={styles.uploadView}>
                <MediaUpload onUploadComplete={handleUploadComplete} />
              </div>
            )}
          </div>
          <div
            className={`${styles.detailsSidebar} ${
              !selectedForPreview ? styles.hidden : ''
            }`}
          >
            {selectedForPreview && (
              <>
                <h4>Detalji Slike</h4>
                <img
                  src={selectedForPreview.fullUrl}
                  alt="Preview"
                  className={styles.detailsPreview}
                />
                <div className={styles.detailsInfo}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="mediaName">Naziv Slike</label>
                    <input
                      id="mediaName"
                      type="text"
                      className={styles.inputField}
                      value={editableName}
                      onChange={(e) => setEditableName(e.target.value)}
                    />
                  </div>
                  {(selectedForPreview.size ?? 0) > 0 && (
                    <p>
                      <strong>Veličina:</strong> {selectedForPreview.size} KB
                    </p>
                  )}
                  <p>
                    <strong>URL:</strong>{' '}
                    <a
                      href={selectedForPreview.fullUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Otvori u novom tabu
                    </a>
                  </p>
                </div>
                <div className={styles.detailsActions}>
                  <ButtonPrimary
                    onClick={handleUpdateName}
                    disabled={
                      isSaving || editableName === selectedForPreview.name
                    }
                  >
                    {isSaving ? 'Čuvanje...' : 'Sačuvaj'}
                  </ButtonPrimary>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeleteClick(selectedForPreview)}
                  >
                    <FaTrash /> Obriši
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <ButtonPrimary
            onClick={handleConfirm}
            disabled={selectedForOutputIds.length === 0}
          >
            {multi
              ? `Izaberi ${selectedForOutputIds.length} slika`
              : 'Izaberi sliku'}
          </ButtonPrimary>
        </div>
      </SimpleModal>
      <ConfirmationModal
        show={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Potvrda brisanja"
        message="Da li ste sigurni da želite trajno da obrišete ovu sliku? Ova akcija se ne može opozvati."
      />
    </>
  );
};

export default MediaLibrarySelector;
